import { Redirect, Route, Switch } from 'react-router-dom';
import { RoutesEnum } from './enums';
import { observer } from 'mobx-react-lite';
import { IRoute } from './types';
import { lazy, useEffect, useState } from 'react';
import { useStores } from '../hooks';
import { getCategories, getCreators, getStripePublicKey } from './api';
import Loader from '../components/Loader/Loader';
import Search from '../pages/Search/Search';
import {
  generateFormattedAddress,
  getCurrentCoords,
  iOS,
  isAppleDevice,
  isChromeBrowser,
  isInstagramInAppBrowser,
  isSafariBrowser
} from './helpers';
import { PaymentMethodViewModel } from './backend/models';
import { getAllUserData } from '../components/Modal/AuthModal/AuthModal.helper';
import {
  APPLE_PAY_METHOD,
  CREDIT_DEBIT_METHOD,
  GOOGLE_PAY_METHOD,
  MY_LOCATION
} from './consts';

const home = lazy(() => import('../pages/Home/Home'));
const userProfile = lazy(() => import('../pages/VendorProfile/VendorProfile'));
const eventsManagement = lazy(() => import('../pages/Events/Events'));
const eventManagement = lazy(
  () => import('../pages/EventManagement/EventManagement')
);
const eventCheckIn = lazy(() => import('../pages/CheckIn/CheckIn'));
const grammyAttendees = lazy(
  () => import('../pages/GrammyAttendees/GrammyAttendees')
);
const grammyGuestList = lazy(
  () => import('../pages/GrammyGuestList/GrammyGuestList')
);
const grammyRegisterList = lazy(
  () => import('../pages/GrammyRegisterList/GrammyRegisterList')
);
const grammyInviteeList = lazy(
  () => import('../pages/GrammyInviteeList/GrammyInviteeList')
);
const grammyRedirectPage = lazy(() => import('../pages/Grammy/Grammy'));

const publicRoutes: IRoute[] = [
  {
    path: RoutesEnum.Root,
    exact: true,
    component: home
  },
  {
    path: RoutesEnum.GrammyUrl,
    exact: true,
    component: grammyRedirectPage
  },
  {
    path: RoutesEnum.GrammyUrlWithInvitation,
    exact: true,
    component: grammyRedirectPage
  },
  {
    path: RoutesEnum.GrammyInvitationUrl,
    exact: true,
    component: home
  },
  {
    path: RoutesEnum.GrammyAttendees,
    exact: true,
    component: grammyAttendees
  },
  {
    path: RoutesEnum.GrammyGuests,
    exact: true,
    component: grammyGuestList
  },
  {
    path: RoutesEnum.GrammmyRegisterList,
    exact: true,
    component: grammyRegisterList
  },
  {
    path: RoutesEnum.GrammyInviteeList,
    exact: true,
    component: grammyInviteeList
  },
  {
    path: RoutesEnum.ShortUrlRedirect,
    exact: true,
    component: home
  },
  {
    path: RoutesEnum.EventsManager,
    component: lazy(() => import('../pages/EventsManager/EventsManager'))
  },
  {
    path: RoutesEnum.Video,
    exact: true,
    component: home
  },
  {
    path: RoutesEnum.VideoComment,
    exact: true,
    component: home
  },
  {
    path: RoutesEnum.Experience,
    exact: true,
    component: lazy(() => import('../pages/Experience/Experience'))
  },
  {
    path: RoutesEnum.Messages,
    exact: true,
    component: home
  },
  {
    path: RoutesEnum.Book,
    component: lazy(() => import('../pages/Book/Book'))
  },
  {
    path: RoutesEnum.EventsManagement,
    component: eventsManagement,
    exact: true
  },
  {
    path: RoutesEnum.EventCheckIn,
    component: eventCheckIn,
    exact: true
  },
  {
    path: RoutesEnum.EventCheckInWithId,
    component: eventCheckIn,
    exact: true
  },
  {
    path: [RoutesEnum.EventManagementSection, RoutesEnum.EventManagement],
    component: eventManagement,
    exact: true
  },
  {
    path: RoutesEnum.Tickets,
    exact: true,
    component: home
  },
  {
    path: RoutesEnum.ResetPassword,
    exact: true,
    component: lazy(() => import('../pages/ResetPassword/ResetPassword'))
  },
  {
    path: RoutesEnum.VendorBookings,
    exact: true,
    component: userProfile
  },
  {
    type: 'redirect',
    path: '/tamaraanthony',
    redirectTo: '/u/tamaraanthony'
  },
  {
    type: 'redirect',
    path: '/gcm',
    redirectTo: '/u/grandcentralmarket'
  },
  {
    path: [RoutesEnum.VendorProfile, '/creators/:id'],
    component: userProfile
  },
  {
    path: RoutesEnum.Categories,
    exact: true,
    component: lazy(() => import('../pages/Category/Category'))
  },
  {
    path: RoutesEnum.Clips,
    exact: true,
    component: lazy(() => import('../pages/Moments/Moments'))
  },
  {
    path: RoutesEnum.ClipsForCategory,
    exact: true,
    component: lazy(() => import('../pages/Moments/Moments'))
  },
  {
    path: RoutesEnum.Videos,
    exact: true,
    component: lazy(() => import('../pages/Videos/Videos'))
  },
  {
    path: [RoutesEnum.Experiences, RoutesEnum.Events, RoutesEnum.NearBy],
    exact: true,
    component: lazy(() => import('../pages/Gigs/Gigs'))
  },
  {
    path: RoutesEnum.Search,
    exact: true,
    component: Search
  },
  {
    path: RoutesEnum.Privacy,
    exact: true,
    component: lazy(() => import('../pages/Privacy/Privacy'))
  },
  {
    path: RoutesEnum.Terms,
    exact: true,
    component: lazy(
      () => import('../pages/TermsAndConditions/TermsAndConditions')
    )
  },
  {
    path: RoutesEnum.LegalTerms,
    exact: true,
    component: lazy(() => import('../pages/LegalTerms/LegalTerms'))
  },
  {
    path: RoutesEnum.About,
    exact: true,
    component: lazy(() => import('../pages/AboutUs/AboutUs'))
  },
  {
    path: RoutesEnum.ExternalIntegrations,
    exact: true,
    component: lazy(
      () => import('../pages/ExternalIntegrations/ExternalIntegrations')
    )
  },
  {
    path: [RoutesEnum.Bookings, RoutesEnum.Booking],
    component: lazy(() => import('../pages/Bookings/Bookings'))
  },
  {
    component: lazy(() => import('../pages/ErrorPage/ErrorPage')),
    path: [RoutesEnum.Error]
  },
  {
    type: 'redirect',
    path: '*',
    redirectTo: RoutesEnum.Error404
  }
];

const noAuthRoutes: IRoute[] = [
  // {
  //   path: RoutesEnum.LogIn,
  //   exact: true,
  //   component: lazy(() => import('../pages/LogIn/LogIn'))
  // }
];

const authRoutes: IRoute[] = [];

const Routes = () => {
  const {
    userStore,
    bookingsStore,
    likedMedia,
    creatorStore,
    followUsers,
    categoryStore,
    locationStore,
    appDataManager,
    stripeStore
  } = useStores();
  const [isLoading, setIsLoading] = useState(true);
  const routes: IRoute[] = !!userStore.user
    ? [...authRoutes, ...publicRoutes]
    : [...noAuthRoutes, ...publicRoutes];

  useEffect(() => {
    if (userStore.user) return;

    appDataManager.loadData();

    if (appDataManager.data) {
      locationStore.saveLocation(
        appDataManager.data.hyperLocal?.fullAddress,
        appDataManager.data.hyperLocal?.coords
      );
    }

    /**
     * Request and update user location if he has granted permissions
     */
    getCurrentCoords(
      async (coords) => {
        //Update hyper local coords if user has selected my location
        if (appDataManager.data.hyperLocal?.fullAddress === MY_LOCATION) {
          locationStore.updateCoords(appDataManager.data.hyperLocal?.coords);
        }

        const addressData = await generateFormattedAddress(coords);

        userStore.saveAddress(
          addressData.formattedAddress,
          addressData.addressResult,
          addressData.latLng
        );
      },
      () => {}
    );

    getCommonData().then(getAuthData);
  }, [userStore.user]);

  const getAuthData = async () => {
    try {
      setIsLoading(true);
      const {
        user,
        userBookings,
        creatorBookings,
        userLikes,
        following,
        paymentMethods
      } = await getAllUserData();

      userStore.saveUserToStore(user);
      bookingsStore.saveToStore(userBookings, creatorBookings);
      likedMedia.saveToStore(userLikes);
      followUsers.saveToStore(following);
      userStore.savePaymentMethods(paymentMethods);

      setIsLoading(false);
    } catch (e: any) {
      if (userStore.user && e.response.status === 401) {
        userStore.saveUserToStore(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCommonData = async () => {
    try {
      const categories = await getCategories();
      categoryStore.saveToStore(categories);

      const creators = await getCreators();
      creatorStore.saveToStore(creators);

      const _paymentMethods: PaymentMethodViewModel[] = [];

      if (isAppleDevice() && (isSafariBrowser() || isInstagramInAppBrowser())) {
        _paymentMethods.push(APPLE_PAY_METHOD);
      }

      if (!iOS() && (isChromeBrowser() || isInstagramInAppBrowser())) {
        _paymentMethods.push(GOOGLE_PAY_METHOD);
      }

      if (!userStore.user) _paymentMethods.push(CREDIT_DEBIT_METHOD);

      userStore.savePaymentMethods(_paymentMethods);
    } catch (e: any) {
      creatorStore.saveToStore([]);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <Switch>
      {routes.map((route, idx) => {
        if (route.type === 'redirect') {
          return (
            <Route
              exact={route.exact}
              path={route.path}
              key={`redirect-${route.path}`}
            >
              <Redirect to={route.redirectTo || RoutesEnum.Root} />
            </Route>
          );
        }
        return <Route key={`route-${idx}`} {...route} />;
      })}
    </Switch>
  );
};

export default observer(Routes);
